import { parseJwt } from "../../../helper/genericFunction";

// SSO callback url : /sm/auth/callback/<source>?access_token=<jwt>
const SSO_CALLBACK_PATH_REGEX = /\/sm\/auth\/callback\/([a-z0-9]+)\/?$/i;

const SSO_TOKEN_KEY = "SSO_LOGIN_TOKEN";
const SSO_TARGET_MODULE_KEY = "SSO_TARGET_MODULE";
const SSO_ERROR_KEY = "SSO_LOGIN_ERROR";

export const SSO_LOGIN_FLOW_TYPE = "supplymint-sso";

// SSO portal the user is sent back to when the sso login fails (per REACT_APP_NODE_ENV).
// When not configured the user is sent back in the browser history (the portal which redirected him).
const SSO_PORTAL_URLS = {
    production: "",
    quality: "",
    development: "",
    sandbox: "",
};

// Callback source -> workspace module. Matched against the module codes first and then
// the module names returned by the server (demand planning has no fixed code on the frontend).
const SSO_SOURCE_MODULES = {
    s2p: { codes: ["DIGIPROC"], names: ["digiproc"] },
    ip: { codes: ["DIGIARS", "ALLOKATOR"], names: ["allokator", "allocator"] },
    dp: { codes: [], names: ["demandplanning"] },
    som: { codes: ["DIGISALES"], names: ["digisales", "salesordermanagement"] },
};

// Function : To capture the sso callback url, has to run before the routers read the url.
// Clears the previous session of the tab, stores the token & the source module and
// replaces the url with the app root so that the hash routing works as usual.
// Supports both the path url (/sm/auth/callback/s2p?access_token=) and the hash url (/#/sm/auth/callback/s2p?access_token=)
export const captureSsoCallback = () => {
    let hashUrl = null;
    try {
        hashUrl = new URL(window.location.hash?.replace(/^#/, "") || "/", window.location.origin);
    } catch (err) {
        hashUrl = null;
    }
    const callbackUrl = [window.location, hashUrl].find(url => SSO_CALLBACK_PATH_REGEX.test(url?.pathname || ""));
    if (!callbackUrl) return false;

    const match = callbackUrl.pathname.match(SSO_CALLBACK_PATH_REGEX);
    const token = new URLSearchParams(callbackUrl.search).get("access_token");
    window.history.replaceState({}, "", "/#/");
    if (!token) return false;

    sessionStorage.clear();
    sessionStorage.setItem(SSO_TOKEN_KEY, token);
    sessionStorage.setItem(SSO_TARGET_MODULE_KEY, match[1]?.toLowerCase());
    return true;
}

// Function : To get the login payload for the pending sso login
export const getSsoLoginPayload = () => {
    const token = sessionStorage.getItem(SSO_TOKEN_KEY);
    if (!token) return null;
    let tokenData = {};
    try {
        tokenData = parseJwt(token);
    } catch (err) {
        console.error("Failed to parse sso token", err);
    }
    return {
        username: tokenData?.user?.username || "",
        password: token,
        flowType: SSO_LOGIN_FLOW_TYPE,
    };
}

// Pending sso login or a failed sso login (the normal / auto login should not run in both cases)
export const hasPendingSsoLogin = () => !!(sessionStorage.getItem(SSO_TOKEN_KEY) || sessionStorage.getItem(SSO_ERROR_KEY));

export const clearSsoLoginToken = () => sessionStorage.removeItem(SSO_TOKEN_KEY);

export const clearSsoLogin = () => {
    sessionStorage.removeItem(SSO_TOKEN_KEY);
    sessionStorage.removeItem(SSO_TARGET_MODULE_KEY);
    sessionStorage.removeItem(SSO_ERROR_KEY);
}

// Function : To mark the sso login as failed, kept in the session so that a refresh does not open the login form
export const setSsoLoginError = (message) => {
    sessionStorage.removeItem(SSO_TOKEN_KEY);
    sessionStorage.removeItem(SSO_TARGET_MODULE_KEY);
    sessionStorage.setItem(SSO_ERROR_KEY, message || "");
}

export const getSsoLoginError = () => sessionStorage.getItem(SSO_ERROR_KEY);

// Function : To send the user back to the sso portal after a failed sso login
export const redirectToSsoPortal = () => {
    clearSsoLogin();
    const portalUrl = SSO_PORTAL_URLS[process.env.REACT_APP_NODE_ENV ?? "production"];
    if (portalUrl) {
        window.location.replace(portalUrl);
    } else {
        window.history.back();
    }
}

// Function : To get (only once) the module code the sso user has to be switched to.
// Returns null when there is no pending sso switch, the module is already active or
// the user does not have access to it.
export const consumeSsoTargetModule = (newModuleData = {}) => {
    const source = sessionStorage.getItem(SSO_TARGET_MODULE_KEY);
    if (!source) return null;
    sessionStorage.removeItem(SSO_TARGET_MODULE_KEY);

    const { codes = [], names = [] } = SSO_SOURCE_MODULES[source] || {};
    const normalize = (value) => value?.toString()?.toLowerCase()?.replace(/[^a-z0-9]/g, "") || "";

    const targetModule = (newModuleData?.availableModules || []).find(item =>
        codes.includes(item?.moduleCode?.toUpperCase?.()) || names.includes(normalize(item?.moduleName))
    );

    if (!targetModule?.moduleCode || targetModule.moduleCode === newModuleData?.activeModule) return null;
    return targetModule.moduleCode;
}
